import { TextMetrics as PixiTextMetrics } from '@pixi/text';
import { Rectangle } from 'pixi.js';

// Type for accessing private static methods on PIXI TextMetrics
// These are internal PIXI methods we need to access for text layout
interface TextMetricsPrivateStatic {
    isNewline(char: string): boolean;
    isBreakingSpace(char: string): boolean;
    tokenize(text: string): string[];
    _context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
}

// Cast to unknown first, then to our interface to bypass private member checks
const TextMetricsExt = PixiTextMetrics as unknown as TextMetricsPrivateStatic;

export const DEFAULT_TEXT_ALPHA = 1;
export const DEFAULT_SCALE = 1;

export function isNewline(text: string): boolean {
    // isNewline is declared as private inside PIXI
    return TextMetricsExt.isNewline(text);
}

export function isBreakingSpace(char: string): boolean {
    // isBreakingSpace is declared as private inside PIXI
    return TextMetricsExt.isBreakingSpace(char);
}

export function tokenize(text: string): string[] {
    // tokenize is declared as private inside PIXI
    return TextMetricsExt.tokenize(text);
}

export function metricsContext(): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
    return TextMetricsExt._context;
}

const measureTextSupport: { cached?: boolean } = {};

/**
 * Whether this environment's canvas 2D context implements real text measurement. False in the
 * Node entrypoints (the video-data server and the video data slicer), whose canvas polyfill mocks
 * only the methods lottie-web needs (`fillRect`/`clearRect`/`createLinearGradient`) and has no
 * `measureText`. Only a positive result is memoized, so an environment observed before its real
 * canvas is ready is not written off for the rest of the process.
 *
 * See also `CanvasRenderer.isTextRenderingSupported`, which answers the same question for
 * text-asset building with its own probe.
 */
export function canMeasureText(): boolean {
    if (measureTextSupport.cached) {
        return true;
    }
    try {
        return (measureTextSupport.cached = typeof metricsContext().measureText === 'function');
    } catch {
        return false;
    }
}

/** @internal Resets the memoized {@link canMeasureText} result. For test isolation only. */
export function _resetCanMeasureTextForTesting(): void {
    delete measureTextSupport.cached;
}

export function getTotalBounds(elements: Rectangle[]) {
    if (elements.length === 0) {
        return Rectangle.EMPTY;
    }
    const box = elements[0].clone();
    for (let i = 1; i < elements.length; i++) {
        if (elements[i].width > 0) {
            box.enlarge(elements[i]);
        }
    }
    return box;
}

/** The radius of the smallest circle that encloses these bounds. */
export function getHalfDiagonal(bounds: Rectangle): number {
    return Math.hypot(bounds.width, bounds.height) / 2;
}

export function calculateRadiusAfterFit(halfDiagonal: number, radiusPadding: number): number {
    return Math.max(0, halfDiagonal + radiusPadding);
}

export function getBoundsIntersection(bounds1: Rectangle, bounds2: Rectangle): Rectangle {
    if (!bounds1.intersects(bounds2)) {
        return Rectangle.EMPTY;
    }
    const x = Math.max(bounds1.x, bounds2.x);
    const y = Math.max(bounds1.y, bounds2.y);
    const width = Math.min(bounds1.x + bounds1.width, bounds2.x + bounds2.width) - x;
    const height = Math.min(bounds1.y + bounds1.height, bounds2.y + bounds2.height) - y;
    return new Rectangle(x, y, width, height);
}
